import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  DelegateApiService,
  RolesApiService,
  ROLE_PANEL_ROUTES,
  ROLE_PRIORITY,
  Role,
  type DelegateDelegationItem,
} from '@angular-workspace/services';
// Canvas routes lazy-load this library from the same app.
// eslint-disable-next-line @nx/enforce-module-boundaries -- shared helpers, not the lazy canvas route
import {
  displayNameFromEmail,
  grantedOwnerScopes,
  normalizeScopeKey,
  rowScopes,
  setAssumeDelegateRole,
  type DelegateStatus,
  type ManageOwnerRow,
} from '@angular-workspace/feature';

export type WorkspaceKind = 'own' | 'delegated';

export interface WorkspaceContext {
  id: string;
  kind: WorkspaceKind;
  name: string;
  role: string;
  roleLabel: string;
  /** Delegated Access subtitle — `delegatorOrganization`. */
  organisationName?: string;
  initials: string;
  /** Avatar background; matches Figma greens/oranges. */
  avatarColor: string;
}

const STORAGE_KEY = 'mahaagx.activeWorkspaceId';
const SIGNED_IN_INITIALS_KEY = 'mahaagx.signedInInitials';

/**
 * This tab keeps the workspace in sessionStorage.
 * "Open link in new tab" starts a tab with empty sessionStorage, so a session
 * cookie (cleared when the browser closes) carries the same id across tabs.
 * ponytail: the cookie is last-writer-wins. A link opened from an older tab
 * follows the latest switch, not that tab. Upgrade: put the id on the href.
 */
export function resolveStoredWorkspaceId(
  sessionValue: string | null,
  cookieHeader: string,
): string | null {
  if (sessionValue) return sessionValue;
  return readCookieValue(cookieHeader, STORAGE_KEY);
}

function readCookieValue(cookieHeader: string, name: string): string | null {
  const prefix = `${name}=`;
  for (const part of (cookieHeader || '').split(';')) {
    const item = part.trim();
    if (!item.startsWith(prefix)) continue;
    try {
      return decodeURIComponent(item.slice(prefix.length)) || null;
    } catch {
      return null;
    }
  }
  return null;
}

const AVATAR_GREEN = '#11634f';
const AVATAR_ORANGE = '#c76e1a';
const AVATAR_COLORS = [AVATAR_ORANGE, AVATAR_GREEN];

const ROLE_LABELS: Record<string, string> = {
  [Role.CONSUMER]: 'Consumer',
  [Role.PROVIDER]: 'Provider',
  [Role.ORG_ADMIN]: 'Organisation Admin',
  [Role.ADMIN]: 'Platform Admin',
  org_admin: 'Organisation Admin',
};

@Injectable({ providedIn: 'root' })
export class WorkspaceContextService {
  private readonly rolesService = inject(RolesApiService);
  private readonly delegateApi = inject(DelegateApiService);

  private ownerRows: ManageOwnerRow[] = [];
  private loaded = false;
  private inFlight: Promise<void> | null = null;
  private fetchSeq = 0;
  /** Avatar letters of the signed-in user. A delegate workspace must not replace them. */
  private signedInInitials = this.readSignedInInitials();

  private readonly activeIdSubject = new BehaviorSubject<string | null>(
    this.readStoredId(),
  );
  readonly activeId$ = this.activeIdSubject.asObservable();

  constructor() {
    setAssumeDelegateRole(this.getActiveId() !== 'own');
  }

  /** Own workspace derived from the logged-in user. */
  getOwnWorkspace(
    name: string,
    initials: string,
    roles: string[],
  ): WorkspaceContext {
    const role = this.rolesService.getHighestRole(roles) || Role.CONSUMER;
    return {
      id: 'own',
      kind: 'own',
      name: name || 'My Workspace',
      role,
      roleLabel: this.roleLabel(role),
      initials: initials || 'U',
      avatarColor: AVATAR_GREEN,
    };
  }

  /**
   * Delegated workspaces from GET /auth/delegation/delegate.
   */
  getDelegatedWorkspaces(): WorkspaceContext[] {
    return this.ownerRows.map((row, index) =>
      this.workspaceFromOwner(row, index),
    );
  }

  async loadDelegatedWorkspaces(force = false): Promise<void> {
    if (this.inFlight) {
      return this.inFlight;
    }
    if (this.loaded && !force) {
      return;
    }
    const seq = ++this.fetchSeq;
    const run = this.fetchDelegatedWorkspaces(seq);
    this.inFlight = run;
    try {
      await run;
    } finally {
      if (this.inFlight === run) {
        this.inFlight = null;
      }
    }
  }

  clearDelegatedWorkspaces(): void {
    this.fetchSeq++;
    this.ownerRows = [];
    this.loaded = false;
    this.inFlight = null;
    this.forgetSignedInInitials();
    this.enterOwnWorkspace();
  }

  /**
   * Saves the signed-in user's letters the first time My Workspace knows them.
   * A delegate session cannot replace them, and neither can a later account payload.
   */
  noteSignedInInitials(initials: string): void {
    if (this.isDelegateSession() || this.signedInInitials) {
      return;
    }
    const value = (initials || '').trim();
    if (!value) {
      return;
    }
    this.signedInInitials = value;
    try {
      sessionStorage.setItem(SIGNED_IN_INITIALS_KEY, value);
    } catch {
      /* storage can be blocked */
    }
  }

  /** Profile-icon letters. Falls back when My Workspace has not been shown yet. */
  avatarInitials(fallback: string): string {
    return this.signedInInitials || fallback;
  }

  /** Return to My Workspace (owner view). Login and logout always start here. */
  enterOwnWorkspace(): void {
    setAssumeDelegateRole(false);
    this.activeIdSubject.next('own');
    this.persistWorkspaceId('own');
  }

  workspaceFromOwner(row: ManageOwnerRow, index = 0): WorkspaceContext {
    const name = (row.name || '').trim() || displayNameFromEmail(row.email);
    const role = this.roleFromOwnerRole(row.ownerRole);
    return {
      id: `delegated-${row.id}`,
      kind: 'delegated',
      name,
      role,
      roleLabel: this.roleLabel(role),
      organisationName: (row.organisationName || '').trim(),
      initials: this.initialsFromName(name),
      avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
    };
  }

  /** Keep the owner in cache so Switch can open that role's panel before a refetch. */
  adoptOwnerWorkspace(row: ManageOwnerRow): WorkspaceContext {
    this.ownerRows = [
      ...this.ownerRows.filter((item) => item.id !== row.id),
      row,
    ];
    this.loaded = true;
    const workspace = this.workspaceFromOwner(row, this.ownerRows.length - 1);
    this.selectWorkspace(workspace);
    return workspace;
  }

  /** Whether the header My Workspace control should show (has any delegated access). */
  hasDelegatedAccess(): boolean {
    return this.getDelegatedWorkspaces().length > 0;
  }

  getActiveId(): string {
    const stored = this.activeIdSubject.value;
    if (!stored || stored === 'own') return 'own';
    const delegated = this.getDelegatedWorkspaces();
    if (delegated.some((ws) => ws.id === stored)) return stored;
    // Keep a just-selected delegated workspace before the list cache is ready.
    if (!this.loaded && stored.startsWith('delegated-')) return stored;
    return 'own';
  }

  isActive(id: string): boolean {
    return this.getActiveId() === id;
  }

  getActiveWorkspace(own: WorkspaceContext): WorkspaceContext {
    const id = this.getActiveId();
    if (id === 'own') return own;
    return this.getDelegatedWorkspaces().find((ws) => ws.id === id) ?? own;
  }

  selectWorkspace(workspace: WorkspaceContext): void {
    setAssumeDelegateRole(workspace.kind === 'delegated');
    this.activeIdSubject.next(workspace.id);
    this.persistWorkspaceId(workspace.id);
  }

  /**
   * Drop an owner from the header cache. When that grant was the active
   * workspace, return to My Workspace and yield the user's own panel home
   * (Delegated Access is not in the own-role sidebar). Returns null to stay.
   */
  exitDelegatedOwner(ownerId: string): string | null {
    const wasActive = this.getActiveId() === `delegated-${ownerId}`;
    this.ownerRows = this.ownerRows.filter((row) => row.id !== ownerId);
    this.loaded = false;
    if (!wasActive) {
      return null;
    }
    this.enterOwnWorkspace();
    return this.rolesService.getUserPanelRoute();
  }

  panelRouteFor(workspace: WorkspaceContext): string {
    return (
      ROLE_PANEL_ROUTES[workspace.role] || ROLE_PANEL_ROUTES[Role.CONSUMER]
    );
  }

  /** True when a delegated owner workspace is active. */
  isDelegateSession(): boolean {
    return this.getActiveId() !== 'own';
  }

  /** Superset stays with the real platform admin. A delegate in that panel does not get it. */
  canViewPlatformSuperset(): boolean {
    return !(
      this.isDelegateSession() &&
      this.getActiveDelegatedPanelRole() === Role.ADMIN
    );
  }

  /** Active delegated workspace, or null when using My Workspace. */
  getActiveDelegatedWorkspace(): WorkspaceContext | null {
    const id = this.getActiveId();
    if (!id.startsWith('delegated-')) return null;
    return this.getDelegatedWorkspaces().find((ws) => ws.id === id) ?? null;
  }

  /** Panel role granted by the active owner (`constraints[].role`). */
  getActiveDelegatedPanelRole(): Role | null {
    const row = this.getActiveOwnerRow();
    if (!row) return null;
    return this.roleFromOwnerRole(row.ownerRole);
  }

  /** Highest-priority grant, or the first grant when priorities are equal. */
  preferredDelegatedWorkspace(): WorkspaceContext | null {
    const list = this.getDelegatedWorkspaces();
    if (!list.length) return null;
    let best = list[0];
    let bestPriority = ROLE_PRIORITY[best.role] ?? 0;
    for (const workspace of list) {
      const priority = ROLE_PRIORITY[workspace.role] ?? 0;
      if (priority > bestPriority) {
        best = workspace;
        bestPriority = priority;
      }
    }
    return best;
  }

  delegatedWorkspaceForRole(role?: string | null): WorkspaceContext | null {
    const list = this.getDelegatedWorkspaces();
    if (!list.length) return null;
    if (role) {
      const match = list.find((workspace) => workspace.role === role);
      if (match) return match;
    }
    return this.preferredDelegatedWorkspace();
  }

  /**
   * Enter a delegated workspace so sidebar/guards treat the session as a
   * delegate panel. Prefers an already-active grant, then a grant matching
   * `preferredRole`, then the highest-role grant.
   */
  activateDelegatedWorkspace(
    preferredRole?: string | null,
  ): WorkspaceContext | null {
    const existing = this.getActiveDelegatedWorkspace();
    if (existing && (!preferredRole || existing.role === preferredRole)) {
      return existing;
    }
    const next = this.delegatedWorkspaceForRole(preferredRole) ?? existing;
    if (!next) return null;
    this.selectWorkspace(next);
    return next;
  }

  /** Owner row for the currently selected delegated workspace. */
  getActiveOwnerRow(): ManageOwnerRow | null {
    const id = this.getActiveId();
    if (!id.startsWith('delegated-')) return null;
    const ownerId = id.slice('delegated-'.length);
    return this.ownerRows.find((row) => row.id === ownerId) ?? null;
  }

  /** Grant id for the active delegated workspace (not sent as `did`). */
  getActiveDelegationId(): string | null {
    const id = this.getActiveId();
    if (!id.startsWith('delegated-')) return null;
    const delegationId = id.slice('delegated-'.length).trim();
    return delegationId || null;
  }

  /** Owner user id (`did` header) for the active delegated workspace. */
  getActiveDelegatorId(): string | null {
    const id = (this.getActiveOwnerRow()?.delegatorId || '').trim();
    return id || null;
  }

  /** Whether the active delegated owner has granted this scope. */
  hasGrantedScope(scope: string): boolean {
    const row = this.getActiveOwnerRow();
    if (!row) return false;
    const wanted = this.scopeAliasKeys(scope);
    const held = [...rowScopes(row), ...grantedOwnerScopes(row)].map((item) =>
      normalizeScopeKey(item),
    );
    return held.some((item) => wanted.has(item));
  }

  /**
   * Exact grant check. `asset-management` does not match `own-asset-management`.
   */
  hasExactGrantedScope(scope: string): boolean {
    const row = this.getActiveOwnerRow();
    if (!row) return false;
    const wanted = normalizeScopeKey(scope);
    if (!wanted) return false;
    return [...rowScopes(row), ...grantedOwnerScopes(row)]
      .map((item) => normalizeScopeKey(item))
      .includes(wanted);
  }

  /** Owners always see it; delegates only with `org-asset-publish`. */
  canShowPublishAssetsTab(): boolean {
    if (!this.isDelegateSession()) return true;
    return this.hasExactGrantedScope('org-asset-publish');
  }

  /**
   * Owners always see it. Delegates see it with `own-asset-management`
   * or `org-asset-management`. `org-asset-publish` does not open this tab.
   */
  canShowAssetRequestTab(): boolean {
    if (!this.isDelegateSession()) return true;
    return this.hasManageAssetsScope();
  }

  /**
   * Manage Assets children for a delegate session.
   * `own-asset-management` / `org-asset-management` → Add Dataset, Add Model,
   * Assets, Access Policy, My Asset Requests (Asset Request tab).
   * `org-asset-publish` → My Asset Requests (Publish Assets tab) only.
   * Owners keep the full list.
   */
  filterManageAssetsChildren<T extends { label: string }>(children: T[]): T[] {
    if (!this.isDelegateSession()) return children;
    const allowed = new Set<string>();
    if (this.hasManageAssetsScope()) {
      allowed.add('Add Dataset');
      allowed.add('Add Model');
      allowed.add('Assets');
      allowed.add('Access Policy');
      allowed.add('My Asset Requests');
    }
    if (this.hasExactGrantedScope('org-asset-publish')) {
      allowed.add('My Asset Requests');
    }
    const filtered = children.filter(
      (child) => child.label === 'separator' || allowed.has(child.label),
    );
    return filtered.filter((child, index, list) => {
      if (child.label !== 'separator') return true;
      const prev = list[index - 1];
      const next = list[index + 1];
      return (
        !!prev &&
        !!next &&
        prev.label !== 'separator' &&
        next.label !== 'separator'
      );
    });
  }

  /** Provider and org-admin asset management. Not platform `asset-management`. */
  private hasManageAssetsScope(): boolean {
    return (
      this.hasExactGrantedScope('own-asset-management') ||
      this.hasExactGrantedScope('org-asset-management')
    );
  }

  private scopeAliasKeys(scope: string): Set<string> {
    const key = normalizeScopeKey(scope);
    const aliases = new Set<string>([key]);
    if (key === 'own asset management' || key === 'asset management') {
      aliases.add('own asset management');
      aliases.add('asset management');
    }
    if (key === 'data access') {
      aliases.add('data access');
      aliases.add('data-access');
    }
    return aliases;
  }

  isActiveOwnerRole(role: string): boolean {
    const row = this.getActiveOwnerRow();
    if (!row) return false;
    return normalizeScopeKey(row.ownerRole || '').includes(
      normalizeScopeKey(role),
    );
  }

  /**
   * Sidebar / panel title: use the granted owner role while acting as a delegate.
   * "Provider" grant → "Provider Delegate", not the current route's panel name.
   */
  panelLabel(baseLabel: string): string {
    const fallback = (baseLabel || '').trim() || 'Consumer';
    if (!this.isDelegateSession()) {
      return fallback;
    }
    const granted = this.getActiveDelegatedWorkspace()?.roleLabel;
    const label = this.compactSidebarRoleLabel(granted || fallback);
    if (/\bdelegate$/i.test(label)) {
      return label;
    }
    return `${label} Delegate`;
  }

  /** Short sidebar names so titles like Org Admin Delegate stay on one line. */
  private compactSidebarRoleLabel(label: string): string {
    const trimmed = (label || '').replace(/\s+delegate$/i, '').trim();
    const key = normalizeScopeKey(trimmed);
    if (key.includes('org')) {
      return 'Org Admin';
    }
    return trimmed;
  }

  roleLabel(role: string): string {
    const key = (role || '').toLowerCase();
    return ROLE_LABELS[key] || ROLE_LABELS[role] || this.titleCase(role);
  }

  private async fetchDelegatedWorkspaces(seq: number): Promise<void> {
    try {
      const { items } = await this.delegateApi.getDelegationsAsDelegate();
      if (seq !== this.fetchSeq) return;
      this.ownerRows = items
        .filter((item) => this.isSwitchableDelegation(item.status))
        .map((item) => this.toOwnerRow(item));
      this.loaded = true;
      setAssumeDelegateRole(this.getActiveId() !== 'own');
      this.activeIdSubject.next(this.activeIdSubject.value);
    } catch (error) {
      if (seq !== this.fetchSeq) return;
      console.error('Failed to load delegated workspaces:', error);
      // A failed refresh must not wipe a list the menu is already showing.
      if (this.ownerRows.length > 0) return;
      this.ownerRows = [];
      this.loaded = false;
      setAssumeDelegateRole(this.getActiveId() !== 'own');
    }
  }

  private isSwitchableDelegation(status: string): boolean {
    const raw = (status || '').toLowerCase();
    if (!raw) return true;
    return (
      !raw.includes('delet') &&
      !raw.includes('reject') &&
      !raw.includes('revok') &&
      !raw.includes('expir')
    );
  }

  private toOwnerRow(item: DelegateDelegationItem): ManageOwnerRow {
    const scopes = (item.scopes ?? []).filter(Boolean);
    const roles = item.constraintRoles?.length
      ? item.constraintRoles
      : item.ownerRole
        ? [item.ownerRole]
        : [];
    const ownerRole =
      this.rolesService.getHighestRole(roles) || item.ownerRole || undefined;
    return {
      id: item.id,
      delegatorId: item.delegatorId || undefined,
      email: item.email,
      name: item.name || undefined,
      organisationName: item.organisationName || undefined,
      ownerRole,
      scope: scopes[0] ?? '',
      scopes,
      status: this.normalizeOwnerStatus(item.status),
      createdAt: item.createdAt,
      expiryAt: item.expiryAt,
    };
  }

  private normalizeOwnerStatus(value: string): Exclude<DelegateStatus, 'all'> {
    const raw = (value || '').toLowerCase().replace(/[_-]+/g, ' ').trim();
    if (raw.includes('delet')) return 'deleted';
    if (raw.includes('reject')) return 'rejected';
    if (raw.includes('revok')) return 'revoked';
    if (raw.includes('expir')) return 'expired';
    if (raw.includes('pend')) return 'pending';
    return 'active';
  }

  roleFromOwnerRole(ownerRole?: string): Role {
    const value = (ownerRole || '').trim().toLowerCase().replace(/-/g, '_');
    if (value.includes('org')) return Role.ORG_ADMIN;
    if (value.includes('provider')) return Role.PROVIDER;
    if (value.includes('admin') || value.includes('cos')) return Role.ADMIN;
    return Role.CONSUMER;
  }

  private initialsFromName(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) return 'U';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private titleCase(value: string): string {
    if (!value) return '';
    return value.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private readSignedInInitials(): string {
    try {
      return sessionStorage.getItem(SIGNED_IN_INITIALS_KEY) || '';
    } catch {
      return '';
    }
  }

  /** Logout drops the saved letters so the next person does not inherit them. */
  private forgetSignedInInitials(): void {
    this.signedInInitials = '';
    try {
      sessionStorage.removeItem(SIGNED_IN_INITIALS_KEY);
    } catch {
      /* storage can be blocked */
    }
  }

  private persistWorkspaceId(id: string): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore storage errors */
    }
    try {
      const secure =
        typeof window !== 'undefined' && window.isSecureContext
          ? '; Secure'
          : '';
      document.cookie = `${STORAGE_KEY}=${encodeURIComponent(id)}; Path=/; SameSite=Lax${secure}`;
    } catch {
      /* ignore storage errors */
    }
  }

  private readStoredId(): string | null {
    let sessionValue: string | null = null;
    try {
      sessionValue = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      sessionValue = null;
    }
    let cookieHeader = '';
    try {
      cookieHeader = document.cookie;
    } catch {
      cookieHeader = '';
    }
    const id = resolveStoredWorkspaceId(sessionValue, cookieHeader);
    if (
      id &&
      (sessionValue !== id || readCookieValue(cookieHeader, STORAGE_KEY) !== id)
    ) {
      this.persistWorkspaceId(id);
    }
    return id;
  }
}
